import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { VerticalTabsVariant } from './asc-vertical-tabs';

/**
 * Loose interface for `asc-tab-panel` to avoid a hard import cycle.
 * The widget works on the DOM element shape, not the class import.
 */
export interface TabPanelLike extends HTMLElement {
  active: boolean;
  disabled: boolean;
  variant: VerticalTabsVariant;
  managed: boolean;
}

/**
 * Public contract for the `asc-vertical-tabs` host element consumed by this
 * controller. Exported so `AscVerticalTabs` can explicitly `implements` it.
 */
export interface VerticalTabsHost extends ReactiveControllerHost, HTMLElement {
  variant: VerticalTabsVariant;
}

/**
 * Manages tab panel orchestration for `asc-vertical-tabs`:
 * - Collects slotted `asc-tab-panel` elements
 * - Propagates `variant`, `managed`, and `aria-label` to child panels
 * - Resolves the initially active panel
 * - Extracts tab-name and count text from panels
 *
 * Co-located with the widget because this controller is tightly coupled
 * to `asc-vertical-tabs` and has no reuse value outside it.
 */
export class VerticalTabsController implements ReactiveController {
  private readonly _host: VerticalTabsHost;

  constructor(host: VerticalTabsHost) {
    this._host = host;
    host.addController(this);
  }

  hostConnected(): void {
    // no-op — slot-based discovery happens on slotchange
  }

  hostDisconnected(): void {
    // no-op
  }

  /** Returns all `asc-tab-panel` children assigned to the default slot. */
  getPanels(): TabPanelLike[] {
    const slot = this._host.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])');
    if (!slot) return [];
    return slot
      .assignedElements({ flatten: true })
      .filter((el): el is TabPanelLike => el.tagName === 'ASC-TAB-PANEL');
  }

  /** Extracts the text content of a panel's `tab-name` slot. */
  getTabNameText(panel: TabPanelLike): string {
    return panel.querySelector('[slot="tab-name"]')?.textContent?.trim() ?? '';
  }

  /** Extracts the text content of a panel's `count` slot. */
  getCountText(panel: TabPanelLike): string {
    return panel.querySelector('[slot="count"]')?.textContent?.trim() ?? '';
  }

  /** Extracts the text content of a panel's `tab-description` slot. */
  getDescriptionText(panel: TabPanelLike): string {
    return panel.querySelector('[slot="tab-description"]')?.textContent?.trim() ?? '';
  }

  /**
   * Synchronises all slotted panels with the widget's current state:
   * - Sets `managed` attribute on each panel
   * - Propagates `variant` to each panel
   * - Sets `aria-label` to the tab-name text for accessible panel naming
   * - Resolves the initially active panel (first non-disabled active, or first non-disabled)
   *
   * @returns The collected panels array.
   */
  syncPanels(): TabPanelLike[] {
    const panels = this.getPanels();

    // Determine initial active panel, ignoring disabled panels as candidates
    const nonDisabledPanels = panels.filter(p => !p.disabled);
    const activeNonDisabled = panels.filter(p => p.active && !p.disabled);

    let activeTarget: TabPanelLike | undefined;

    if (activeNonDisabled.length > 0) {
      activeTarget = activeNonDisabled[0];
    } else if (nonDisabledPanels.length > 0) {
      activeTarget = nonDisabledPanels[0];
    }

    // Ensure at most one active panel, and never leave a disabled panel active
    for (const panel of panels) {
      panel.active = panel === activeTarget;
    }

    // Propagate widget state to each panel
    for (const panel of panels) {
      panel.variant = this._host.variant;
      panel.managed = true;
      panel.setAttribute('aria-label', this.getTabNameText(panel));
    }

    return panels;
  }
}
